import { environment } from '@env/environment';
import axios from 'axios';

export interface ClosedReasons {
  id?: string;
  clientId: string;
  enabled: boolean;
  allowAdditionalDetails: boolean;
  reason: string;
  archived: boolean;
  ordinal: number;
  isInErrorState?: boolean;
}

export type NotificationPreferences = {
  notifyForNewUnassignedConversations: boolean;
};
export type NotificationPreferencesOutput = {
  message: string;
};

export const client = axios.create({
  baseURL: environment.twoWayURLBase,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
  responseType: 'json',
});

export const configClient = axios.create({
  baseURL: environment.configURLBase,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
  responseType: 'json',
});

export const ClosedReasonsService = {
  /** Create Closed Reason
   *
   * @param clientId the clientId.
   * @param enabled the enabled.
   * @param allowAdditionalDetails the allowAdditionalDetails.
   * @param reason the reason.
   * @param archived the archived.
   * @param ordinal the ordinal.
   */
  createClosedReasons: async (
    clientId: string,
    enabled,
    allowAdditionalDetails,
    reason,
    archived,
    ordinal
  ): Promise<ClosedReasons> => {
    const url = `/client/${clientId}/conversation-closed-reasons`;
    const reqBody = {
      clientId,
      enabled,
      allowAdditionalDetails,
      reason,
      archived,
      ordinal
    };
    const { data } = await client.post(url, reqBody);
    return data;
  },
  /** Update Closed Reason
   *
   * @param clientId the clientId.
   * @param enabled the enabled.
   * @param allowAdditionalDetails the allowAdditionalDetails.
   * @param reason the reason.
   * @param archived the archived.
   */
  updateClosedReasons: async (
    clientId: string,
    id: string,
    enabled,
    allowAdditionalDetails,
    reason,
    archived,
    ordinal,
  ): Promise<ClosedReasons> => {
    const url = `/client/${clientId}/conversation-closed-reasons/${id}`;
    const reqBody = {
      clientId,
      enabled,
      allowAdditionalDetails,
      reason,
      archived,
      ordinal,
    };
    const { data } = await client.put(url, reqBody);
    return data;
  },

  /** Get Closed Reasons
   *
   *  @param clientId the clientId.
   */
  getClosedReasons: async (clientId: string): Promise<ClosedReasons[]> => {
    const url = `/client/${clientId}/conversation-closed-reasons`;
    const {
      data: { data },
    } = await client.get(url);
    return data.map((closedReason) => {
      return {
        id: closedReason.id,
        clientId: closedReason.clientId,
        enabled: closedReason.enabled,
        allowAdditionalDetails: closedReason.allow_additional_details,
        reason: closedReason.reason,
        updatedAt: closedReason.updated_at,
        createdAt: closedReason.created_at,
        archived: closedReason.archived,
        ordinal: closedReason.ordinal,
      }
    })
  },

  /** Get Client Config
   *
   * @param clientId the clientId.
   */
  getClient: async (clientId: string): Promise<any> => {
    const url = `/client/${clientId}`;
    const { data } = await configClient.get(url);
    return data;
  },

  /***  Get Notification Preferences
   * 
   * Get the notification preferences for a client.
   * @param clientId the ID of the client to get the notification preferences.
   * @returns the notification preferences
   */
  getNotificationPreferences: async (clientId: string) => {
    const url = `/client/${clientId}/configs/notification_preferences`;
    try {
      const { data } = await client.get<NotificationPreferences>(url);
      return data;
    } catch (error) {
      const { status, data } = error?.response || {};
      if (status && status === 404 && data?.message && data.message === 'email preferences not set') {
        return { notifyForNewUnassignedConversations: false };
      }
      throw error;
    }
  },

  /***  Save Notification Preferences
   * 
   * Save the notification preferences for a client.
   * @param clientId the ID of the client to save the notification preferences.
   * @param preferences the notification preferences to save.
   * @returns the saved notification preferences
   */
  saveNotificationPreferences: async (clientId: string, preferences: NotificationPreferences) => {
    const url = `/client/${clientId}/configs/notification_preferences`;
    const { data } = await client.post<NotificationPreferencesOutput>(url, preferences);
    return data;
  },
};
